// Service catalogue — used by /diensten overview and individual /diensten/[slug] pages
// All copy is Dutch and SEO-optimized: clear H1 keyword, FAQ schema, internal links.
import {
  Brain,
  Megaphone,
  Search,
  PenTool,
  Globe,
  FileText,
  Zap,
  Target,
  Wand2,
  Gauge,
  BarChart3,
  Facebook,
  Music2,
  Linkedin,
  Wrench,
  Link2,
  LayoutTemplate,
  PenLine,
  MousePointerClick,
  Rocket,
  Compass,
  Bot,
  ListChecks,
  Radar,
  Newspaper,
  LineChart,
} from "lucide-react";

export const services = [
  {
    slug: "ai-marketing",
    icon: Brain,
    title: "AI Marketing",
    short: "Slimmere campagnes met AI-segmentatie en predictive targeting.",
    metaTitle: "AI Marketing Bureau | Slimmere Campagnes met AI",
    metaDescription:
      "AI marketing voor MKB & ZZP: predictive targeting, automatische segmentatie en machine-learning campagnes die meer leads opleveren. Door UPPIT.",
    h1: "AI Marketing dat écht presteert",
    intro:
      "Klassieke marketing is duur en traag. AI marketing van UPPIT versnelt elk onderdeel van je campagne: van doelgroepanalyse tot copy en optimalisatie. We combineren slimme algoritmes met menselijke strategie zodat je geen geld meer verbrandt aan campagnes die niet werken.",
    sections: [
      {
        h2: "Wat is AI marketing?",
        body:
          "AI marketing gebruikt machine learning om beslissingen te automatiseren die marketeers normaal handmatig nemen. Denk aan welke advertentie aan welke gebruiker te tonen, welke productcombinaties beste converteren of wat het ideale moment is om een mail te sturen. Voor MKB en ZZP betekent dit: meer impact zonder een groot team.",
      },
      {
        h2: "Wat krijg je bij UPPIT?",
        body:
          "We zetten AI in op vier vlakken — geen hype, alleen tools die je omzet meetbaar verhogen:",
        cards: [
          { icon: Target, title: "Doelgroepvoorspelling", desc: "Predictive analytics bepaalt wie je ideale klant is — vóórdat je budget uitgeeft." },
          { icon: Wand2, title: "AI-creatie", desc: "Advertentievarianten en e-mailflows in uren in plaats van weken." },
          { icon: Gauge, title: "Slimme optimalisatie", desc: "Automatische bid- en budgetsturing binnen Meta en Google." },
          { icon: BarChart3, title: "Realtime dashboards", desc: "Direct inzicht in CAC, LTV en ROAS — geen maandrapport nodig." },
        ],
      },
      {
        h2: "Voor wie is het geschikt?",
        body:
          "Voor ondernemers met minimaal €500/mnd advertentiebudget die structureel willen groeien. Hoe meer data, hoe beter het AI-model — maar we starten ook prima zonder. Combineer AI marketing met onze SEO-optimalisatie en content optimalisatie voor maximale schaalbaarheid.",
      },
    ],
    benefits: [
      "Lager CPL door scherpere targeting",
      "Snellere creatieve iteratie via AI",
      "Predictive analytics op je customer journey",
      "Doorlopende optimalisatie zonder handmatig werk",
    ],
    faqs: [
      {
        q: "Vervangt AI marketing mijn marketeer?",
        a: "Nee. AI versnelt en versterkt je marketeer — strategie, merk en creatieve richting blijven mensenwerk. Wij combineren beide.",
      },
      {
        q: "Werkt het ook voor lokale ondernemers?",
        a: "Zeker. Lokale targeting met AI op postcode-niveau is een van onze sterkste tools voor MKB.",
      },
      {
        q: "Wat is de minimale investering?",
        a: "Vanaf ongeveer €750 per maand inclusief mediabudget en bureaufee zien we al meetbare groei.",
      },
    ],
    related: ["seo-optimalisatie", "social-media-advertising", "ai-optimalisatie"],
  },
  {
    slug: "social-media-advertising",
    icon: Megaphone,
    title: "Social Media Advertising",
    short: "Meta, TikTok & LinkedIn ads die ROAS opleveren — geen vanity metrics.",
    metaTitle: "Social Media Advertising | Meta, TikTok & LinkedIn Ads",
    metaDescription:
      "Social media advertising via UPPIT: Meta, TikTok en LinkedIn campagnes met focus op ROAS, niet op vanity metrics. Direct meer leads en omzet.",
    h1: "Social media advertising met focus op winst",
    intro:
      "Likes betalen je rekeningen niet. Wij bouwen advertentiecampagnes op Meta, TikTok en LinkedIn die direct gekoppeld zijn aan jouw conversiedoelen — leads, sales of afspraken. Met scherpe doelgroepen, AI-creatieven en doorlopende A/B-tests halen we het maximale uit elke euro.",
    sections: [
      {
        h2: "Welke kanalen zetten we in?",
        body:
          "We kiezen kanalen op basis van je doelgroep en verwachte ROAS — niet op basis van trends of wat we 'leuk' vinden:",
        cards: [
          { icon: Facebook, title: "Meta", desc: "Facebook + Instagram: koning voor B2C en lokale dienstverleners." },
          { icon: Music2, title: "TikTok", desc: "Enorm bereik voor productmerken en een jong publiek." },
          { icon: Linkedin, title: "LinkedIn", desc: "Dé plek voor B2B-leadgeneratie en zakelijke beslissers." },
        ],
      },
      {
        h2: "Onze aanpak",
        body:
          "Iedere campagne start met een fundament: doelgroep, conversie-tracking (server-side via Conversions API), creatieve hoeken en landing pages. Daarna ad-creatives in 4-8 varianten, A/B-tests op elke touchpoint en wekelijkse rapportages met heldere CAC- en ROAS-cijfers.",
      },
      {
        h2: "Resultaten die we leveren",
        body:
          "Klanten zien gemiddeld 3-8x ROAS op Meta-campagnes en 2-4x op TikTok binnen de eerste 90 dagen. Hoe beter je product-market fit, hoe sneller we schalen. Geen wonderlijst — wel transparante data.",
      },
    ],
    benefits: [
      "Multi-platform: Meta, TikTok, LinkedIn, Pinterest",
      "Server-side tracking (Conversions API)",
      "AI-creatieven & A/B-testing automation",
      "Wekelijkse ROAS-rapportage",
    ],
    faqs: [
      {
        q: "Hoe snel zie ik resultaat?",
        a: "Eerste leads/sales meestal binnen 7-14 dagen. Echte schaalvoordelen na 30-60 dagen wanneer het algoritme leert.",
      },
      {
        q: "Heb je een minimaal budget nodig?",
        a: "Vanaf €500/mnd mediabudget kunnen we testen. Voor échte schaalvoordelen adviseren we €1.500+ per maand.",
      },
      {
        q: "Mag ik mijn account meenemen?",
        a: "Altijd. Je behoudt eigendom van je advertentie-accounts en data. Wij krijgen alleen toegang.",
      },
    ],
    related: ["ai-marketing", "content-optimalisatie", "webdesign-ai"],
  },
  {
    slug: "seo-optimalisatie",
    icon: Search,
    title: "SEO Optimalisatie",
    short: "Domineer Google. Technisch + content gedreven, met meetbare ranking groei.",
    metaTitle: "SEO Optimalisatie Bureau | Hoger in Google Ranken",
    metaDescription:
      "SEO optimalisatie voor MKB en ZZP: technische SEO, contentstrategie en linkbuilding. Stijg in Google met UPPIT — meetbaar, transparant, eerlijk.",
    h1: "SEO optimalisatie die je zichtbaar maakt",
    intro:
      "98% van zoekende klanten klikt op de top-3 van Google. Sta jij daar niet, dan bestaan ze voor jou niet. Onze SEO-aanpak combineert technische optimalisatie, sterke content en autoriteitsopbouw — geen trucjes, geen gokwerk, alleen meetbare groei.",
    sections: [
      {
        h2: "De drie pijlers van SEO",
        body:
          "Alle drie zijn nodig om écht te ranken — daarom pakken wij ze alle drie tegelijk aan:",
        cards: [
          { icon: Wrench, title: "Technische SEO", desc: "Snelle laadtijden, mobiel-eerst design, schone HTML, schema markup en zero crawl-errors." },
          { icon: FileText, title: "Content SEO", desc: "Zoekwoordenonderzoek, intent-mapping en content die zoekvragen beter beantwoordt dan je concurrent." },
          { icon: Link2, title: "Off-page SEO", desc: "Gerichte linkbuilding van autoriteit-sites in jouw branche." },
        ],
      },
      {
        h2: "Hoe pakken we dit aan?",
        body:
          "Maand 1: technische audit + zoekwoorden-strategie. Maand 2-3: technische fixes + eerste content-batch. Maand 3-6: doorlopende content + linkbuilding. Resultaten: meestal vanaf maand 3 zichtbaar, met grote sprongen tussen maand 6-12. SEO is geen sprint, het is een fundament.",
      },
      {
        h2: "Waarom UPPIT?",
        body:
          "We schrijven geen content om content. Elke pagina heeft een commercieel doel. Combineer dit met onze webdesign AI dienst voor een fundamenteel snel en SEO-proof platform en je hebt een ranking-machine.",
      },
    ],
    benefits: [
      "Volledige technische SEO-audit",
      "Zoekwoordenonderzoek met search intent",
      "Maandelijkse content-batches",
      "Whitehat linkbuilding NL & internationaal",
    ],
    faqs: [
      {
        q: "Wanneer zie ik SEO resultaat?",
        a: "Eerste rankings: 2-3 maanden. Sterke groei: vanaf 6 maanden. Domineren in concurrente niches: 9-12 maanden.",
      },
      {
        q: "Kan ik garantie krijgen op #1 in Google?",
        a: "Niemand kan dat eerlijk garanderen. We garanderen wel structureel meer organisch verkeer en meetbare KPI's.",
      },
      {
        q: "Heb ik een nieuwe website nodig?",
        a: "Vaak helpt het, maar niet altijd. We doen eerst een audit voordat we adviseren.",
      },
    ],
    related: ["content-optimalisatie", "webdesign-ai", "ai-optimalisatie"],
  },
  {
    slug: "webdesign-ai",
    icon: PenTool,
    title: "Webdesign AI",
    short: "AI-gestuurde websites die converteren. Razendsnel, SEO-proof, klantgericht.",
    metaTitle: "Webdesign AI | Snelle Websites die Converteren",
    metaDescription:
      "Webdesign AI van UPPIT: AI-gestuurde websites die laden in <2s, geoptimaliseerd voor SEO en gebouwd om te converteren. Webflow, WordPress of headless.",
    h1: "Webdesign AI: websites gemaakt om te verkopen",
    intro:
      "Een website is geen visitekaartje meer — het is je belangrijkste verkoopkanaal. Met onze webdesign AI methode bouwen we websites die snel laden, geoptimaliseerd zijn voor zoekmachines én bezoekers omzetten in klanten. AI versnelt elk deel: van wireframes tot copy tot design.",
    sections: [
      {
        h2: "Wat maakt onze webdesign AI uniek?",
        body:
          "Bij UPPIT gebruiken we AI om concepten in dagen te bouwen, niet weken — zonder in te leveren op vakmanschap:",
        cards: [
          { icon: LayoutTemplate, title: "Wireframes op doel", desc: "Gegenereerd op basis van jouw conversiedoelen — niet op een standaard sjabloon." },
          { icon: PenLine, title: "Copy die verkoopt", desc: "AI-geschreven, door mensen geredigeerd én A/B-getest." },
          { icon: MousePointerClick, title: "Heatmap-validatie", desc: "Design gevalideerd met heatmap-simulatie vóórdat we bouwen." },
          { icon: Rocket, title: "Schaalbare bouw", desc: "Gebouwd in Webflow of WordPress voor maximale schaalbaarheid." },
        ],
      },
      {
        h2: "Onze stack",
        body:
          "We bouwen in Webflow voor visuele controle of WordPress voor flexibiliteit. Beide in combinatie met snelle hosting (Cloudflare, Vercel), Tailwind CSS, schema markup en Core Web Vitals-optimalisatie. Voor complexe maatwerk bouwen we headless met React.",
      },
      {
        h2: "Conversion-first design",
        body:
          "Mooi is niet genoeg. Elke pagina heeft één primair doel: leads, verkopen of afspraken. We gebruiken bewezen UX-patronen, snelheidsoptimalisatie en doelgerichte CTA's. Combineer met onze SEO-dienst en content optimalisatie voor maximale prestaties.",
      },
    ],
    benefits: [
      "Live in 4-8 weken (vs 4-6 maanden bij concurrenten)",
      "<2s laadtijd, mobile-first",
      "AI-generated copy + handmatig geredigeerd",
      "Volledig schaalbaar in Webflow of WordPress",
    ],
    faqs: [
      {
        q: "Wat kost een nieuwe website?",
        a: "Onze pakketten starten vanaf ~€2.500 voor een ZZP-website tot €15.000+ voor maatwerk e-commerce. Eerlijk advies eerst.",
      },
      {
        q: "Mag ik de site zelf beheren?",
        a: "Absoluut. Webflow en WordPress zijn beide gebruiksvriendelijk. We trainen je en leveren documentatie.",
      },
      {
        q: "Werken jullie met designers?",
        a: "Ja. Elk project heeft een UX/UI-designer + AI-tools. We mixen menselijk vakmanschap met AI-snelheid.",
      },
    ],
    related: ["webflow-wordpress", "seo-optimalisatie", "ai-marketing"],
  },
  {
    slug: "webflow-wordpress",
    icon: Globe,
    title: "Webflow & WordPress",
    short: "Bouw op het juiste fundament. Schaalbaar, beheersbaar en SEO-proof.",
    metaTitle: "Webflow & WordPress Bureau | Schaalbare Websites",
    metaDescription:
      "Webflow en WordPress development door UPPIT. Custom thema's, headless CMS, e-commerce en migraties. Snelle, beheersbare websites die schalen.",
    h1: "Webflow & WordPress: kies het juiste platform",
    intro:
      "Het verschil tussen een groeiende site en een vastgelopen project zit vaak in de platform-keuze. Webflow geeft je visuele controle en blazing-fast performance. WordPress geeft maximale flexibiliteit en een gigantisch ecosysteem. Wij helpen je kiezen — en bouwen het netjes.",
    sections: [
      {
        h2: "Wanneer Webflow?",
        body:
          "Kies Webflow als je een marketing-site, portfolio of premium B2B-platform bouwt waar visuele controle, snelheid en CMS-gemak centraal staan. Inhoud wordt door je team gemanaged in een interface die voelt als Notion. Ideaal voor 5-50 pagina's.",
      },
      {
        h2: "Wanneer WordPress?",
        body:
          "Kies WordPress voor e-commerce (WooCommerce), kennisbanken, multi-language sites of platforms met complexe rolverdeling. Het ecosysteem is enorm — wij richten alleen de plug-ins in die echt waarde toevoegen, en bouwen indien nodig custom thema's en blocks.",
      },
      {
        h2: "Migraties & onderhoud",
        body:
          "Vastgelopen op een legacy-platform? Wij migreren naar Webflow of WordPress zonder verlies van SEO-rankings, met 301-redirects, content audits en hostingoverdracht. Daarna bieden we maandelijks onderhoud (updates, backups, security).",
      },
    ],
    benefits: [
      "Eerlijk advies tussen Webflow & WordPress",
      "Custom development zonder bloat",
      "SEO-veilige migraties",
      "Onderhoudscontracten beschikbaar",
    ],
    faqs: [
      {
        q: "Wat als ik later wil switchen?",
        a: "We bouwen alle sites met portabele content en clean code. Migreren tussen platforms is altijd mogelijk.",
      },
      {
        q: "Hosting inbegrepen?",
        a: "Webflow inclusief. WordPress hosten we op Cloudways/Kinsta voor maximale snelheid (optioneel).",
      },
      {
        q: "Hebben jullie WooCommerce-ervaring?",
        a: "Ja. Volledige webshops, betalingen via Mollie/Stripe, koppelingen met externe ERP's en marketing-tools.",
      },
    ],
    related: ["webdesign-ai", "seo-optimalisatie", "content-optimalisatie"],
  },
  {
    slug: "content-optimalisatie",
    icon: FileText,
    title: "Content Optimalisatie",
    short: "Sterke content die rankt én verkoopt. Door mensen, versneld door AI.",
    metaTitle: "Content Optimalisatie | SEO Content die Converteert",
    metaDescription:
      "Content optimalisatie voor je website en blog. SEO-content, AI-versneld, redactioneel uitgewerkt door UPPIT. Rank hoger en converteer beter.",
    h1: "Content optimalisatie die rankt én verkoopt",
    intro:
      "De meeste websites hebben gemiddelde content die ergens tussen plek 30 en 100 in Google staat. Wij maken content die in de top-10 hoort — én die mensen daadwerkelijk converteert. Door zoekintentie-analyse, AI-versnelde drafts en menselijke redactie.",
    sections: [
      {
        h2: "Onze content-methode",
        body:
          "Vijf stappen van zoekwoord tot publicatie — AI-versneld, door mensen afgemaakt:",
        cards: [
          { icon: Search, title: "Zoekwoordenonderzoek", desc: "Per pagina of cluster brengen we de exacte zoekvraag in kaart." },
          { icon: Compass, title: "Search intent", desc: "Informatie, vergelijking of transactie — de intentie bepaalt de vorm." },
          { icon: Bot, title: "AI first draft", desc: "Gebaseerd op de top-10 concurrenten plus jouw expertise." },
          { icon: PenLine, title: "Menselijke redactie", desc: "On-brand copy, feitelijk gecheckt en SEO-geoptimaliseerd." },
          { icon: Link2, title: "Publicatie + interne links", desc: "Elke nieuwe pagina versterkt de rest van je site." },
        ],
      },
      {
        h2: "Welke content schrijven we?",
        body:
          "Landingspagina's, blogs, productpagina's, case studies, e-books en e-mailflows. Alles in jouw tone of voice, geoptimaliseerd voor zowel mensen als zoekmachines. Combineer met onze AI optimalisatie zodat je content ook gevonden wordt door ChatGPT en Perplexity.",
      },
      {
        h2: "Bestaande content optimaliseren",
        body:
          "Heb je al een blog? Vaak verdubbelen we organisch verkeer door bestaande artikelen te updaten — zonder nieuwe te schrijven. We doen content audits, identificeren ranking-quick-wins en herschrijven gericht.",
      },
    ],
    benefits: [
      "AI-versneld, redactioneel uitgewerkt",
      "Search intent gestuurd",
      "Volledig SEO-geoptimaliseerd",
      "Content audits + herschrijven",
    ],
    faqs: [
      {
        q: "Hoe vaak verschijnt nieuwe content?",
        a: "Standaard 4-12 stukken per maand, afhankelijk van pakket. Audits leveren vaak meer impact dan volume.",
      },
      {
        q: "Klinkt het 'echt' of als AI?",
        a: "Echt. AI is een snelheidstool, geen vervanger. Onze redacteuren herschrijven elk stuk in jouw tone of voice.",
      },
      {
        q: "Mag ik input geven op onderwerpen?",
        a: "Verplicht zelfs. Jouw expertise + onze SEO-data = winnende content.",
      },
    ],
    related: ["seo-optimalisatie", "ai-optimalisatie", "webdesign-ai"],
  },
  {
    slug: "ai-optimalisatie",
    icon: Zap,
    title: "AI Optimalisatie (GEO)",
    short: "Word zichtbaar in ChatGPT, Perplexity & Gemini. De nieuwe SEO is hier.",
    metaTitle: "AI Optimalisatie (GEO) | Zichtbaar in ChatGPT & Perplexity",
    metaDescription:
      "AI optimalisatie / GEO door UPPIT: word zichtbaar in ChatGPT, Perplexity, Gemini en Google AI Overviews. De nieuwe vorm van SEO voor 2026.",
    h1: "AI Optimalisatie (GEO): de nieuwe SEO is hier",
    intro:
      "Steeds meer mensen googlen niet meer — ze vragen het ChatGPT, Perplexity of Gemini. Sta jij daar niet tussen? Dan bestaan jouw producten of diensten niet voor die gebruikers. AI Optimalisatie (ook wel GEO of Generative Engine Optimization) zorgt dat AI-modellen jouw merk noemen wanneer mensen iets in jouw branche zoeken.",
    sections: [
      {
        h2: "Wat is GEO precies?",
        body:
          "Generative Engine Optimization is het optimaliseren van je content, autoriteit en data zodat AI-modellen jou citeren in hun antwoorden. Het gaat verder dan klassieke SEO: het draait om feiten, structuur, schema markup, citaties op autoriteit-sites en machine-leesbare content.",
      },
      {
        h2: "Onze GEO-methode",
        body:
          "Zo maken we jouw merk stap voor stap zichtbaar in AI-antwoorden:",
        cards: [
          { icon: Radar, title: "Prompt-onderzoek", desc: "Welke vragen stellen klanten in jouw branche aan AI-assistenten?" },
          { icon: Search, title: "Zichtbaarheids-audit", desc: "Waar sta je nu in ChatGPT, Perplexity, Gemini en Google AI Overviews?" },
          { icon: ListChecks, title: "Content-optimalisatie", desc: "FAQ-schema, factuele structuur en autoriteit-bronvermelding." },
          { icon: Newspaper, title: "Citaties opbouwen", desc: "PR, gastblogs en branche-publicaties die AI-modellen vertrouwen." },
          { icon: LineChart, title: "Monitoren & bijsturen", desc: "Maandelijks meten met gespecialiseerde GEO-tools." },
        ],
      },
      {
        h2: "Waarom nu starten?",
        body:
          "AI-zoekgedrag groeit explosief in 2026. Wie nu investeert in GEO bouwt een voorsprong die concurrenten over 12 maanden niet meer inhalen. Combineer met content optimalisatie en SEO voor een volledige zichtbaarheidsstrategie.",
      },
    ],
    benefits: [
      "Zichtbaar in ChatGPT, Perplexity, Gemini",
      "Schema markup & FAQ-implementatie",
      "Autoriteit-citaties via PR & gastposts",
      "Maandelijkse GEO-rapportage",
    ],
    faqs: [
      {
        q: "Vervangt GEO klassieke SEO?",
        a: "Nee, ze vullen elkaar aan. SEO blijft cruciaal — Google verwerkt 8 miljard queries per dag. GEO is de extra laag voor AI-zoekers.",
      },
      {
        q: "Hoe meten jullie succes?",
        a: "We tracken citaten in ChatGPT, Perplexity en Google AI Overviews via gespecialiseerde tools (Profound, Otterly).",
      },
      {
        q: "Kunnen jullie dit voor elke branche?",
        a: "Ja, met name effectief voor B2B, professionele dienstverlening, e-commerce en complexe productverkoop.",
      },
    ],
    related: ["seo-optimalisatie", "content-optimalisatie", "ai-marketing"],
  },
];

export const getServiceBySlug = (slug) =>
  services.find((s) => s.slug === slug) || null;
